import { Modal, Link } from '@atlassian/learning-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { FEEDBACK_PAGE_FF } from 'src/constants';
import { NextCourse } from 'src/pages/LessonPage/types/nextCourse';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { getCatalogUrl, getCourseUrl, getLessonUrl, withPrefix } from 'src/utils/getUrl';
import {
  useCourseCompletionRate,
  useLearningPathCompletionRate,
  useProgressState,
} from 'src/store/progress/progressStore';
import { LessonLoaderProps } from 'src/pages/LessonPage/loader';
import { getSlug } from 'src/utils/slug';

interface BadgeModalProps {
  nextCourse: NextCourse | null;
  badgeImg: string;
  isLessonRecentlyCompleted: boolean;
}

const COMPLETED_RATE = 100;

enum ButtonLabel {
  FIND_YOUR_NEXT_COURSE = 'Find your next course',
  CONTINUE_TO_NEXT_COURSE = 'Continue to next course',
}

export const BadgeModal: React.FC<BadgeModalProps> = ({ nextCourse, badgeImg, isLessonRecentlyCompleted }) => {
  const navigate = useNavigate();
  const params = useParams();

  const { course, learningPath } = useLoaderData() as LessonLoaderProps;

  const [{ isLoadingProgressForCourses }] = useProgressState();
  const [courseCompletionRate] = useCourseCompletionRate(course);
  const [learningPathCompletionRate] = useLearningPathCompletionRate(learningPath);

  const [isOpen, setIsOpen] = useState(false);

  const isCourseModalRendered = useRef(false);
  const isPathModalRendered = useRef(false);

  const isFeedbackPageAccessible = FeatureGates.checkGate(FEEDBACK_PAGE_FF);

  const isLearningPathCompleted = !!(learningPath && learningPathCompletionRate === COMPLETED_RATE);
  const isCourseCompleted = courseCompletionRate === COMPLETED_RATE;

  const shouldHide = !!(params?.pathId && !learningPath);

  useEffect(() => {
    if (!isLessonRecentlyCompleted) {
      isCourseModalRendered.current = false;
      isPathModalRendered.current = false;
    }
  }, [isLessonRecentlyCompleted]);

  useEffect(() => {
    if (isLoadingProgressForCourses || shouldHide) {
      return;
    }

    const isCourseCompletionModal =
      !learningPath && isCourseCompleted && isLessonRecentlyCompleted && !isCourseModalRendered.current;

    const isNextCourseModal =
      learningPath && isCourseCompleted && isLessonRecentlyCompleted && !isCourseModalRendered.current;

    const isLearningPathCompletionModal =
      isLearningPathCompleted && isLessonRecentlyCompleted && !isPathModalRendered.current;

    if (isCourseCompletionModal || isNextCourseModal || isLearningPathCompletionModal) {
      setIsOpen(true);
    }

    if (isCourseCompletionModal || isNextCourseModal) {
      isCourseModalRendered.current = true;
    }

    if (isLearningPathCompletionModal) {
      isPathModalRendered.current = true;
    }
  }, [
    isLearningPathCompleted,
    isCourseCompleted,
    isLoadingProgressForCourses,
    isLessonRecentlyCompleted,
    shouldHide,
    learningPath,
  ]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const redirectToCatalog = useCallback(() => {
    setIsOpen(false);
    navigate(getCatalogUrl());
  }, [navigate]);

  const goToNextCourse = useCallback(() => {
    setIsOpen(false);

    navigate(
      getLessonUrl({
        pathSlug: getSlug(learningPath),
        courseSlug: nextCourse?.slug,
        lessonSlug: nextCourse?.nextLessonSlug || '',
      }),
    );
  }, [learningPath, navigate, nextCourse?.nextLessonSlug, nextCourse?.slug]);

  const generateButtonLabelForFirstDialog = useMemo(() => {
    if (nextCourse && !isLearningPathCompleted) {
      return ButtonLabel.CONTINUE_TO_NEXT_COURSE;
    }

    return ButtonLabel.FIND_YOUR_NEXT_COURSE;
  }, [isLearningPathCompleted, nextCourse]);

  const handleRedirectForFirstDialog = useMemo(() => {
    if (nextCourse && !isLearningPathCompleted) {
      return goToNextCourse;
    }

    return redirectToCatalog;
  }, [goToNextCourse, isLearningPathCompleted, nextCourse, redirectToCatalog]);

  if (!isOpen || shouldHide) {
    return null;
  }

  const courseSlug = params?.courseId;
  const learningPathSlug = params?.pathId;
  const allLearningPathsHubPart = getLearningPathsHubPart(location.pathname);

  const surveyLink = withPrefix(getCourseUrl(courseSlug!, learningPathSlug, true), allLearningPathsHubPart);

  const surveyText = isFeedbackPageAccessible && (
    <p>
      Help us improve this course by taking a{' '}
      <Link label="short survey" link={surveyLink} onClick={() => window.open(surveyLink, '_blank', 'noopener')} />.
    </p>
  );

  if (isLearningPathCompleted) {
    return (
      <ModalTransition>
        <Modal
          heading="Congrats"
          subheading={<p>You earned a course badge, and completed the learning path {learningPath?.title}!</p>}
          surveyText={surveyText}
          imageLink={badgeImg}
          closeModal={closeModal}
          actionButton={{
            label: 'Find your next course',
            onClick: redirectToCatalog,
          }}
          width={720}
        />
      </ModalTransition>
    );
  }

  return (
    <ModalTransition>
      <Modal
        heading="You earned a badge!"
        subheading={
          <p>Great work! You earned a badge by completing this course. All badges are saved in your profile.</p>
        }
        surveyText={surveyText}
        imageLink={badgeImg}
        closeModal={closeModal}
        actionButton={{
          label: generateButtonLabelForFirstDialog,
          onClick: handleRedirectForFirstDialog,
        }}
        width={720}
      />
    </ModalTransition>
  );
};
